import { useMemo, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import type { FilterDefinition, FilterState } from './types'

/**
 * Parses a filter value from URL search params based on filter type.
 */
function parseFilterValue(type: 'search' | 'multiselect', raw: string | null): unknown {
  if (raw === null) return type === 'multiselect' ? [] : ''

  if (type === 'multiselect') {
    return raw.split(',').filter(Boolean)
  }
  return raw
}

/**
 * Serializes a filter value for URL search params.
 */
function serializeFilterValue(type: 'search' | 'multiselect', value: unknown): string | null {
  if (type === 'multiselect') {
    const arr = value as string[]
    return arr.length > 0 ? arr.join(',') : null
  }
  const str = value as string
  return str || null
}

export interface UseFiltersReturn<T> {
  /** The data after applying all active filters */
  filteredData: T[]
  /** Current filter state parsed from URL */
  filterState: FilterState
  /** Update a single filter's value */
  setFilter: (id: string, value: unknown) => void
  /** Clear a single filter */
  clearFilter: (id: string) => void
  /** Clear all filters */
  clearAllFilters: () => void
  /** Number of currently active filters */
  activeFilterCount: number
}

/**
 * A reusable hook for filtering data with URL-synced state.
 *
 * Features:
 * - Filters are stored in URL search params (shareable, bookmarkable)
 * - Supports search (text) and multiselect filter types
 * - Data filtering is memoized for performance
 *
 * @param data - The source data array to filter
 * @param filters - Array of filter definitions
 * @returns Filtered data and filter management functions
 */
export function useFilters<T>(data: T[], filters: FilterDefinition<T>[]): UseFiltersReturn<T> {
  const [searchParams, setSearchParams] = useSearchParams()

  // Parse filter state from URL
  const filterState = useMemo(() => {
    const state: FilterState = {}
    for (const filter of filters) {
      const raw = searchParams.get(filter.id)
      state[filter.id] = parseFilterValue(filter.type, raw)
    }
    return state
  }, [searchParams, filters])

  // Apply filters to data
  const filteredData = useMemo(() => {
    return data.filter((item) => {
      for (const filter of filters) {
        const value = filterState[filter.id]
        if (!filter.match(item, value)) {
          return false
        }
      }
      return true
    })
  }, [data, filters, filterState])

  // Count active filters
  const activeFilterCount = useMemo(() => {
    let count = 0
    for (const filter of filters) {
      const value = filterState[filter.id]
      if (filter.type === 'multiselect') {
        if ((value as string[]).length > 0) count++
      } else {
        if (value) count++
      }
    }
    return count
  }, [filters, filterState])

  // Set a single filter
  const setFilter = useCallback(
    (id: string, value: unknown) => {
      const filter = filters.find((f) => f.id === id)
      if (!filter) return

      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          const serialized = serializeFilterValue(filter.type, value)
          if (serialized === null) {
            next.delete(id)
          } else {
            next.set(id, serialized)
          }
          return next
        },
        { replace: true }
      )
    },
    [filters, setSearchParams]
  )

  // Clear a single filter
  const clearFilter = useCallback(
    (id: string) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          next.delete(id)
          return next
        },
        { replace: true }
      )
    },
    [setSearchParams]
  )

  // Clear all filters
  const clearAllFilters = useCallback(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        for (const filter of filters) {
          next.delete(filter.id)
        }
        return next
      },
      { replace: true }
    )
  }, [filters, setSearchParams])

  return {
    filteredData,
    filterState,
    setFilter,
    clearFilter,
    clearAllFilters,
    activeFilterCount,
  }
}
